// Quién retira el vehículo (v12.6.4, pedido de Rodrigo para Grumaq): nombre y DNI de quien
// retira, elegido de la lista de personas del cliente (se van guardando solas) o "Otro".
// Se usa al entregar (Taller y Turnos) y para completar o corregir desde Remitos.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MagnifyingGlassIcon, PlusIcon, UserIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

export interface PersonaRetiro { id?: string; nombre: string; dni: string; dniFormateado?: string; usos?: number }
export interface DatosRetiro { nombre: string; dni: string }

const soloDigitos = (s: string) => String(s || '').replace(/\D/g, '');
const conPuntos = (dni: string) => soloDigitos(dni).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const sinAcentos = (s: string) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
// Mismas reglas que el server (server/utils/datosRetiro.js)
const nombreValido = (s: string) => { const n = s.replace(/\s+/g, ' ').trim(); return n.length >= 3 && /[a-záéíóúñü]/i.test(n); };
const dniValido = (s: string) => { const d = soloDigitos(s); return d.length >= 6 && d.length <= 9; };

interface Props {
  open: boolean;
  titulo: string;
  subtitulo?: string;
  textoConfirmar: string;
  personas: PersonaRetiro[];
  actual?: DatosRetiro | null;
  onConfirmar: (datos: DatosRetiro) => void;
  onCancelar: () => void;
}

export const RetiroDialog: React.FC<Props> = ({ open, titulo, subtitulo, textoConfirmar, personas, actual, onConfirmar, onCancelar }) => {
  const [busqueda, setBusqueda] = useState('');
  const [elegida, setElegida] = useState<PersonaRetiro | null>(null);
  const [otro, setOtro] = useState(false);
  const [nombre, setNombre] = useState('');
  const [dni, setDni] = useState('');
  const buscador = useRef<HTMLInputElement>(null);

  // Al abrir: arranca con la persona actual (corrección) o vacío
  useEffect(() => {
    if (!open) return;
    setBusqueda('');
    const yaCargada = actual ? personas.find((p) => soloDigitos(p.dni) === soloDigitos(actual.dni)) : null;
    setElegida(yaCargada || null);
    const sinLista = personas.length === 0;
    setOtro(sinLista || (!!actual && !yaCargada));
    setNombre(actual && !yaCargada ? actual.nombre : '');
    setDni(actual && !yaCargada ? actual.dni : '');
  }, [open, actual, personas]);

  const filtradas = useMemo(() => {
    const q = sinAcentos(busqueda.trim());
    const qd = soloDigitos(busqueda);
    if (!q) return personas;
    return personas.filter((p) => sinAcentos(p.nombre).includes(q) || (qd.length >= 2 && soloDigitos(p.dni).includes(qd)));
  }, [busqueda, personas]);

  const pasarAOtro = () => {
    // Lo que se escribió en el buscador sirve de arranque: números al DNI, letras al nombre
    const q = busqueda.trim();
    if (q && soloDigitos(q).length === q.replace(/[\s.]/g, '').length) { setDni(q); setNombre(''); }
    else { setNombre(q); setDni(''); }
    setElegida(null);
    setOtro(true);
  };

  const datos: DatosRetiro | null = otro
    ? (nombreValido(nombre) && dniValido(dni) ? { nombre: nombre.replace(/\s+/g, ' ').trim(), dni: soloDigitos(dni) } : null)
    : (elegida ? { nombre: elegida.nombre, dni: soloDigitos(elegida.dni) } : null);

  const confirmar = () => { if (datos) onConfirmar(datos); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancelar(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
          {subtitulo && <DialogDescription>{subtitulo}</DialogDescription>}
        </DialogHeader>

        {!otro && (
          <div className="space-y-2">
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                ref={buscador}
                autoFocus
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (filtradas.length === 1) setElegida(filtradas[0]);
                    else if (filtradas.length === 0) pasarAOtro();
                  }
                }}
                placeholder="Buscar por nombre o DNI"
                aria-label="Buscar persona que retira"
                className="pl-9"
              />
            </div>
            <div className="max-h-64 overflow-y-auto rounded-md border" role="listbox" aria-label="Personas que retiran">
              {filtradas.map((p) => {
                const sel = elegida?.id ? elegida.id === p.id : soloDigitos(elegida?.dni || '') === soloDigitos(p.dni);
                return (
                  <button
                    key={p.id || p.dni}
                    type="button"
                    role="option"
                    aria-selected={sel}
                    onClick={() => setElegida(p)}
                    onDoubleClick={() => onConfirmar({ nombre: p.nombre, dni: soloDigitos(p.dni) })}
                    className={`flex w-full items-center gap-3 border-b px-3 py-2.5 text-left last:border-b-0 hover:bg-accent ${sel ? 'bg-accent' : ''}`}
                  >
                    <UserIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{p.nombre}</span>
                      <span className="block text-xs text-muted-foreground">DNI {p.dniFormateado || conPuntos(p.dni)}</span>
                    </span>
                    {sel && <CheckIcon className="h-4 w-4 shrink-0 text-primary" />}
                  </button>
                );
              })}
              {filtradas.length === 0 && (
                <p className="px-3 py-3 text-sm text-muted-foreground">No hay nadie con ese nombre o DNI en la lista.</p>
              )}
              <button
                type="button"
                onClick={pasarAOtro}
                className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm font-medium text-primary hover:bg-accent"
              >
                <PlusIcon className="h-4 w-4 shrink-0" />
                Otro: cargar nombre y DNI
              </button>
            </div>
          </div>
        )}

        {otro && (
          <div className="space-y-3">
            {personas.length === 0 && (
              <p className="text-sm text-muted-foreground">Todavía no hay nadie en la lista de este cliente. Quien cargues ahora queda guardado para la próxima.</p>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="retiro-nombre">Nombre y apellido</Label>
              <Input id="retiro-nombre" autoFocus={!nombre} value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Ej: Juan Pérez" autoComplete="off" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="retiro-dni">DNI</Label>
              <Input
                id="retiro-dni"
                autoFocus={!!nombre && !dni}
                inputMode="numeric"
                value={dni}
                onChange={(e) => setDni(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirmar(); } }}
                placeholder="Ej: 30.123.456"
                autoComplete="off"
              />
              {dni && !dniValido(dni) && <p className="text-xs text-destructive">El DNI tiene que tener 7 u 8 números.</p>}
            </div>
            {personas.length > 0 && (
              <button type="button" onClick={() => setOtro(false)} className="text-sm text-primary hover:underline">
                Volver a la lista
              </button>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onCancelar}>Cancelar</Button>
          <Button onClick={confirmar} disabled={!datos}>{textoConfirmar}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Ficha del cliente: las personas que retiran, con la opción de sacar a alguien de la lista. */
export const PersonasRetiroLista: React.FC<{ clientId: string }> = ({ clientId }) => {
  const [personas, setPersonas] = useState<PersonaRetiro[] | null>(null);
  useEffect(() => {
    let vivo = true;
    axios.get(`/api/clients/${clientId}/retiro-personas`)
      .then((r) => { if (vivo) setPersonas(r.data?.personas || []); })
      .catch(() => { if (vivo) setPersonas([]); });
    return () => { vivo = false; };
  }, [clientId]);

  const quitar = async (p: PersonaRetiro) => {
    try {
      await axios.delete(`/api/clients/${clientId}/retiro-personas/${p.id}`);
      setPersonas((prev) => (prev || []).filter((x) => x.id !== p.id));
      toast.success(`${p.nombre} ya no aparece en la lista`);
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'No se pudo sacar de la lista');
    }
  };

  if (personas === null) return null;
  return (
    <div className="mt-3 rounded-md border">
      <p className="border-b px-3 py-2 text-xs font-medium text-muted-foreground">
        Personas que retiran ({personas.length})
      </p>
      {personas.length === 0 ? (
        <p className="px-3 py-2.5 text-sm text-muted-foreground">Se van agregando solas a medida que retiran.</p>
      ) : (
        <ul className="max-h-48 overflow-y-auto">
          {personas.map((p) => (
            <li key={p.id} className="flex items-center gap-3 border-b px-3 py-2 last:border-b-0">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm">{p.nombre}</span>
                <span className="block text-xs text-muted-foreground">DNI {p.dniFormateado || conPuntos(p.dni)}{p.usos ? ` · ${p.usos} ${p.usos === 1 ? 'retiro' : 'retiros'}` : ''}</span>
              </span>
              <button type="button" onClick={() => quitar(p)} className="text-xs text-muted-foreground hover:text-destructive">
                Quitar
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

type Pedido = {
  titulo: string;
  subtitulo?: string;
  textoConfirmar: string;
  personas: PersonaRetiro[];
  actual?: DatosRetiro | null;
  resolver: (d: DatosRetiro | undefined) => void;
};

/**
 * pedirRetiro(otId): antes de entregar. Si el cliente no pide los datos devuelve null (se
 * entrega como siempre); si los pide abre el cartel y devuelve los datos, o undefined si
 * cancelaron. editarRetiroRemito(remitoId): completa o corrige quién retiró en un remito.
 */
export function useRetiro() {
  const [pedido, setPedido] = useState<Pedido | null>(null);

  const abrir = (p: Omit<Pedido, 'resolver'>) =>
    new Promise<DatosRetiro | undefined>((resolve) => setPedido({ ...p, resolver: resolve }));

  const pedirRetiro = async (otId: string, subtitulo?: string): Promise<DatosRetiro | null | undefined> => {
    let info: any = null;
    try { info = (await axios.get(`/api/reception/${otId}/retiro`)).data; } catch { return null; }
    if (!info?.pide) return null;
    return abrir({ titulo: '¿Quién retira el vehículo?', subtitulo, textoConfirmar: 'Confirmar retiro', personas: info.personas || [] });
  };

  const editarRetiroRemito = async (remitoId: string, subtitulo?: string): Promise<DatosRetiro | null> => {
    let info: any;
    try { info = (await axios.get(`/api/remitos/${remitoId}/retiro`)).data; }
    catch (e: any) { toast.error(e.response?.data?.error || 'No se pudieron leer los datos de retiro'); return null; }
    const datos = await abrir({ titulo: 'Quién retiró el vehículo', subtitulo, textoConfirmar: 'Guardar', personas: info.personas || [], actual: info.actual });
    if (!datos) return null;
    try {
      await axios.put(`/api/remitos/${remitoId}/retiro`, datos);
      toast.success('Listo, quedó cargado quién retiró');
      return datos;
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'No se pudo guardar quién retiró');
      return null;
    }
  };

  const cerrar = (d: DatosRetiro | undefined) => { pedido?.resolver(d); setPedido(null); };

  const dialogoRetiro = (
    <RetiroDialog
      open={!!pedido}
      titulo={pedido?.titulo || ''}
      subtitulo={pedido?.subtitulo}
      textoConfirmar={pedido?.textoConfirmar || 'Confirmar'}
      personas={pedido?.personas || []}
      actual={pedido?.actual}
      onConfirmar={(d) => cerrar(d)}
      onCancelar={() => cerrar(undefined)}
    />
  );

  return { pedirRetiro, editarRetiroRemito, dialogoRetiro };
}
